/**
 * Date-grain display helpers for AI Explore (and anywhere a query groups a date
 * column by a grain). The backend truncates a grained date to the period start
 * (e.g. month → "2022-11-01"), which is misleading to show verbatim. These
 * helpers render the period the way people read it ("Nov 2022", "Q4 2022",
 * "2022") and derive a grain-aware axis label ("Book End Date" + month →
 * "Book Month").
 */

const _MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const _GRAIN_WORD = { year: 'Year', quarter: 'Quarter', month: 'Month', week: 'Week', day: 'Day' };

/** Format a period-start date value for the given grain. Returns the original
 * value unchanged when it isn't a parseable date. Parsed as UTC so a
 * "2022-11-01" period start never slips to the previous day/month. */
export function formatDateByGrain(value, grain) {
  if (value === null || value === undefined || value === '') return value;
  const s = String(value);
  // Allow "2022", "2022-11", and full ISO dates.
  let iso = s;
  if (/^\d{4}$/.test(s)) iso = `${s}-01-01`;
  else if (/^\d{4}-\d{2}$/.test(s)) iso = `${s}-01`;
  const d = new Date(iso.length > 10 ? iso : `${iso}T00:00:00Z`);
  if (isNaN(d.getTime())) return value;
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  switch (grain) {
    case 'year': return String(y);
    case 'quarter': return `Q${Math.floor(m / 3) + 1} ${y}`;
    case 'month': return `${_MONTHS[m]} ${y}`;
    case 'week': return `Wk of ${_MONTHS[m]} ${d.getUTCDate()}, ${y}`;
    case 'day': return `${_MONTHS[m]} ${d.getUTCDate()}, ${y}`;
    default: return value;
  }
}

/** Derive a grain-aware label from a date field's display name:
 * strips a trailing date phrase ("… End Date", "… Date", "… Dt") and appends
 * the grain word, so "Book End Date" + month → "Book Month". */
export function grainDimLabel(baseLabel, grain) {
  const g = _GRAIN_WORD[grain];
  if (!g || !baseLabel) return baseLabel;
  const stripped = String(baseLabel)
    .replace(/\s*(end|start|begin(?:ning)?|effective)?\s*(date|dt)$/i, '')
    .trim();
  return `${stripped || baseLabel} ${g}`;
}

export default { formatDateByGrain, grainDimLabel };
